'use client'

import Image from 'next/image'
import Modal from 'react-modal'
import { useState } from 'react'
import DOMPurify from 'src/_helpers/dompurify'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Typography from 'src/components/design-system/Typography'
import { AudioPlayer as APlayer } from 'src/components/AudioPlayer'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
import TranscriptionIcon from 'public/icons/interface/transcription.svg'
import { getLocalizedThumb } from 'src/_helpers/getLocalizedThumb'

export default function AudioPlayer({ block, locale }) {
  const translateEntry = useTranslateEntry()
  const [activeModal, setActiveModal] = useState(null)
  const translation = block.translations?.[locale] || {}
  const mainTitle = translation?.title
  const alt = translation?.alt
  const thumb = getLocalizedThumb(block, 'audio', locale)
  const thumbUrl = thumb ? getMediaUrl(thumb.url, thumb.version) : null
  const fileUrl = getMediaUrl(block?.audioFile?.url)
  const transcription = translation?.transcription ? DOMPurify.sanitize(translation.transcription) : null

  const featureBadges = [
    transcription && {
      key: 'transcription',
      label: translateEntry('T_TRANSCRIPTION'),
      icon: TranscriptionIcon,
      onClick: () => setActiveModal('transcription'),
      ariaLabel: translateEntry('T_OPEN_TRANSCRIPTION'),
    },
  ].filter(Boolean)

  return (
    <section className={`bg-basic-white ${block.type.value}`} id={`${block.uuid}`}>
      <div className="mx-auto flex w-full max-w-screen-2xl flex-col gap-32 d:gap-64 px-16 d:px-40 py-64">
        <div className="flex xl:max-w-[750px] max-w-665 flex-col gap-16">
          {mainTitle && (
            <Typography variant="h2-bold" color="text-dark-blue-dark-blue1">
              {mainTitle}
            </Typography>
          )}

          {thumbUrl && (
            <Image
              src={thumbUrl}
              width={thumb.width}
              height={thumb.height}
              alt={alt || mainTitle || ' '}
              className="h-auto w-full object-cover"
            />
          )}
        </div>

        <div className="flex flex-col gap-16">
          {fileUrl && (
            <div className="w-full max-w-4xl overflow-hidden rounded-1 bg-style3 shadow-[0_4px_14px_rgba(0,0,0,0.08)]">
              <APlayer
                audio={fileUrl}
                title={alt || mainTitle || ''}
                className="max-w-none"
                style={{
                  '--audio-bg': 'transparent',
                  '--audio-border': 'none',
                  '--audio-filter': 'none',
                  '--audio-controls-color': 'var(--c-dark-blue1)',
                  '--audio-slider-track-bg': 'var(--c-dark-blue6)',
                  '--audio-slider-progress-bg': 'var(--c-dark-blue1)',
                  '--audio-button-size': '48px',
                  '--media-focus-ring-color': 'var(--c-focus-color)',
                }}
              />
            </div>
          )}

          {featureBadges.length > 0 && (
            <div className="flex flex-wrap gap-16">
              {featureBadges.map((feature) => (
                <button
                  key={feature.key}
                  onClick={feature.onClick}
                  className="inline-flex cursor-pointer items-center gap-6 rounded bg-basic-white px-10 py-4 text-left shadow-[0_4px_14px_rgba(0,0,0,0.08)]"
                  aria-haspopup="dialog"
                  aria-label={feature.ariaLabel}
                >
                  <feature.icon aria-hidden="true" />
                  <Typography variant="button-l-bold" color="text-dark-blue-dark-blue2" className="max-t:hidden">
                    {feature.label}
                  </Typography>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {transcription && (
        <Modal
          isOpen={activeModal === 'transcription'}
          onRequestClose={() => setActiveModal(null)}
          contentLabel={translateEntry('T_TRANSCRIPTION_MODAL', true)}
          className="fixed left-1/2 top-1/2 w-[min(95vw,960px)] -translate-x-1/2 -translate-y-1/2 bg-basic-white p-24 outline-none"
          overlayClassName="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        >
          <div
            className="text-block-content body-b2 text-dark-blue-dark-blue1"
            dangerouslySetInnerHTML={{ __html: transcription }}
          />
        </Modal>
      )}
    </section>
  )
}
